import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, ArrowLeft, User } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/lib/cart";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const { add, count } = useCart();

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#1A1A1A]" data-testid="shop-page">
      <header className="flex items-center justify-between px-6 md:px-12 py-6 border-b border-[#C5A059]/20 bg-white">
        <div className="flex items-center gap-4">
          <Link to="/" data-testid="back-to-home-link" aria-label="Back to home"
            className="text-[#4A4A4A] hover:text-[#9E7B3A] transition-colors duration-300">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <img src="/assets/logo.png" alt="Australian Luxe" className="w-10 h-10 rounded-full" />
          <div>
            <p className="font-serif text-lg leading-tight">Australian Luxe</p>
            <p className="text-[9px] tracking-[0.3em] uppercase text-[#9E7B3A]">Luxury Skincare</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/account" data-testid="account-link"
            className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase hover:text-[#9E7B3A] transition-colors duration-300">
            <User className="w-4 h-4" /> Account
          </Link>
          <Link to="/shop/cart" data-testid="cart-link"
            className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase hover:text-[#9E7B3A] transition-colors duration-300">
            <ShoppingBag className="w-4 h-4" /> Cart ({count})
          </Link>
        </div>
      </header>

      <div className="bg-[#1A1A1A] text-white text-center text-[10px] tracking-[0.3em] uppercase py-2.5" data-testid="shipping-notice">
        Free express shipping Australia-wide on orders over $300
      </div>

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-14">
        <p className="text-xs tracking-[0.5em] uppercase text-[#9E7B3A] mb-4">The Collection</p>
        <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tight mb-14">Skincare, <span className="italic text-[#9E7B3A]">elevated</span></h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16" data-testid="product-grid">
          {products.map((p, i) => (
            <motion.div key={p.id}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
              className="group" data-testid={`product-card-${p.id}`}>
              <Link to={`/shop/product/${p.id}`} data-testid={`product-link-${p.id}`}>
                <div className="relative overflow-hidden bg-white border border-[#C5A059]/15 mb-5">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name}
                      className="w-full aspect-[4/5] object-cover transition-transform duration-700 group-hover:scale-105" />
                  ) : (
                    <div className="w-full aspect-[4/5] flex flex-col items-center justify-center gap-3 bg-[#FAFAFA]">
                      <img src="/assets/logo.png" alt="" className="w-16 h-16 rounded-full opacity-40" />
                      <span className="text-[10px] tracking-[0.3em] uppercase text-[#9E7B3A]/70">Product image coming soon</span>
                    </div>
                  )}
                  <span className="absolute top-4 left-4 text-[9px] tracking-[0.25em] uppercase bg-white/90 text-[#9E7B3A] px-3 py-1.5">
                    {p.stock === 0 ? "Sold Out" : p.badge}
                  </span>
                </div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#9E7B3A] mb-1.5">{p.category}</p>
                <h3 className="font-serif text-xl font-light group-hover:text-[#9E7B3A] transition-colors duration-300">{p.name}</h3>
              </Link>
              <p className="text-sm text-[#4A4A4A] mt-1 mb-4">
                {p.variants?.length ? `${p.size} · Choose $50–$200` : `${p.size} · $${p.price.toFixed(2)} AUD`}
              </p>
              {p.variants?.length ? (
                <Link to={`/shop/product/${p.id}`} data-testid={`choose-amount-${p.id}`}
                  className="block text-center w-full border border-[#1A1A1A] text-xs tracking-[0.25em] uppercase py-3 hover:bg-[#1A1A1A] hover:text-white transition-colors duration-300">
                  Choose Amount
                </Link>
              ) : (
                <button onClick={() => { add(p); toast.success(`${p.name} added to cart`); }}
                  disabled={p.stock === 0}
                  data-testid={`add-to-cart-${p.id}`}
                  className="w-full border border-[#1A1A1A] text-xs tracking-[0.25em] uppercase py-3 hover:bg-[#1A1A1A] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-current transition-colors duration-300">
                  {p.stock === 0 ? "Sold Out" : "Add to Cart"}
                </button>
              )}
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
}
